// Shared Web Audio plumbing for the app's short UI sounds (chat chime, notification chime).
//
// One AudioContext for the whole app, deliberately: browsers cap how many a page may create,
// and each one needs its own unlocking gesture. Sharing it means the single gesture listener at
// startup unlocks every sound at once.

let audioCtx = null;

const getAudioContext = () => {
    if (audioCtx) return audioCtx;
    const AudioContextClass = window.AudioContext || window.webkitAudioContext;
    if (!AudioContextClass) return null;
    audioCtx = new AudioContextClass();
    return audioCtx;
}

// Chrome (and other browsers) refuse to run an AudioContext until the page has seen a real
// user gesture (click/tap/key), silently leaving it "suspended" otherwise — with no error and
// no permission prompt to warn about it. Call this from a one-time gesture listener at app
// startup so the context is already unlocked by the time a real notification needs to play.
export const unlockAudioContext = () => {
    try {
        const ctx = getAudioContext();
        if (ctx && ctx.state === 'suspended') ctx.resume().catch(() => {});
    } catch (error) { /* audio unavailable — skip silently */ }
}

// Lets callers know whether a real user gesture has already unlocked audio playback, so they
// can decide whether it's worth prompting for one (e.g. a banner asking to enable chat sounds).
export const isAudioUnlocked = () => audioCtx?.state === 'running';

/**
 * Plays a short sequence of synthesized tones — no audio file to ship, load or fetch
 * cross-origin, and each sound is defined by its notes alone.
 *
 * @param {Array}  notes           [{ freq, start, duration }], seconds relative to now
 * @param {string} [options.type]  oscillator waveform; the timbre that distinguishes one sound
 *                                 from another as much as the notes do
 * @param {number} [options.volume] peak gain, 0..1
 */
export const playTones = (notes, { type = 'sine', volume = 0.2 } = {}) => {
    try {
        const ctx = getAudioContext();
        if (!ctx) return;
        if (ctx.state === 'suspended') ctx.resume().catch(() => {});

        const now = ctx.currentTime;

        notes.forEach(({ freq, start, duration }) => {
            const oscillator = ctx.createOscillator();
            const gain = ctx.createGain();
            oscillator.type = type;
            oscillator.frequency.value = freq;
            oscillator.connect(gain);
            gain.connect(ctx.destination);

            const startTime = now + start;
            gain.gain.setValueAtTime(0, startTime);
            gain.gain.linearRampToValueAtTime(volume, startTime + 0.01);
            gain.gain.exponentialRampToValueAtTime(0.001, startTime + duration);

            oscillator.start(startTime);
            oscillator.stop(startTime + duration + 0.02);
        });
    } catch (error) { /* audio unavailable or blocked by the browser — skip silently */ }
}

export default getAudioContext;
